"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, Leaf, Droplets, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface MushroomData {
  id: string;
  name: string;
  botanical: string;
  tag: string;
  benefit: string;
  description: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  accentIcon: typeof Sparkles;
}
const mushrooms: MushroomData[] = [{
  id: "maitake",
  name: "Maitake",
  botanical: "Grifola frondosa",
  tag: "Elasticidad & Vitalidad",
  benefit: "Rico en betaglucanos y minerales esenciales",
  description: "Conocido en la tradición japonesa como el hongo danzante, el Maitake aporta hidratación profunda y antioxidantes que protegen la barrera cutánea frente al estrés ambiental cotidiano.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/maitake_botanical.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/maitake_botanical.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/maitake_botanical.png-wvc-sizes",
  imageAlt: "Extracto botánico de hongo Maitake en luz natural",
  accentIcon: Sparkles
}, {
  id: "reishi",
  name: "Reishi",
  botanical: "Ganoderma lucidum",
  tag: "Calma & Restauración",
  benefit: "Triterpenos y polisacáridos calmantes",
  description: "Apreciado durante siglos como el hongo de la longevidad, calma la piel sensible, reduce el enrojecimiento y favorece un descanso reparador cuando forma parte de tu ritual nocturno.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/reishihomepage.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/reishihomepage.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/reishihomepage.png-wvc-sizes",
  imageAlt: "a cluster of whole dried Reishi mushrooms with glossy mahogany-red tops and visible growth rings, resting on a rustic wood slab in natural sunlight",
  accentIcon: Leaf
}, {
  id: "shiitake",
  name: "Shiitake",
  botanical: "Lentinula edodes",
  tag: "Luminosidad & Tono",
  benefit: "Ácido kójico natural y aminoácidos",
  description: "Célebre por su contenido natural de ácido kójico y vitamina D vegetal, ayuda a unificar el tono de la piel y devolverle una luminosidad suave y descansada en cada aplicación.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/shiitake_botanical.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/shiitake_botanical.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/shiitake_botanical.png-wvc-sizes",
  imageAlt: "Textura orgánica de hongos Shiitake frescos",
  accentIcon: Droplets
}];
export default function Ingredients() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="educación-de-ingredientes" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 lg:py-28 overflow-hidden">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-14">
        {/* Ritual Hairline Divider with Center Dot */}
        <div className="relative flex items-center justify-center mb-14 md:mb-20">
          <div className="w-full h-px bg-border" />
          <div className="absolute px-4 bg-background flex items-center gap-2">
            <span className="size-2 rounded-full bg-primary/60" />
            <span className="text-xs uppercase tracking-widest font-sans font-semibold text-muted-foreground">
              Herbolaria Micológica
            </span>
            <span className="size-2 rounded-full bg-primary/60" />
          </div>
        </div>

        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-20">
          <motion.p initial={prefersReducedMotion ? {} : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-xs md:text-sm font-semibold tracking-widest uppercase text-primary mb-3">
            Sabiduría Ancestral & Ciencia Botánica
          </motion.p>
          <motion.h2 initial={prefersReducedMotion ? {} : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground leading-[1.2] mb-5">
            Tres hongos esenciales para nutrir tu piel y calma interior
          </motion.h2>
          <motion.p initial={prefersReducedMotion ? {} : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-base sm:text-lg text-foreground/80 leading-relaxed font-sans">
            Seleccionamos extractos puros cultivados con respeto al ciclo natural, integrando sus fitonutrientes y antioxidantes en texturas reconfortantes.
          </motion.p>
        </div>

        {/* Asymmetric 3-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 items-stretch">
          {mushrooms.map((item, index) => {
          const Icon = item.accentIcon;
          return <motion.article key={item.id} data-index={index} initial={prefersReducedMotion ? {} : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.55,
            delay: index * 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} className={`flex flex-col justify-between rounded-3xl bg-card border border-border p-6 lg:p-8 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-300 ${index === 1 ? "md:-translate-y-3 md:border-primary/30 md:bg-secondary/40" : ""}`}>
                <div>
                  {/* Organic Pill Image Container */}
                  <div className="relative w-full aspect-[4/5] rounded-md overflow-hidden bg-muted/40 mb-6 border border-border/60 mx-auto max-w-[260px] md:max-w-full">
                    <img src={item.imageSrc} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-500 ease-out hover:scale-105" />
                    <div className="absolute top-4 right-4 bg-background/90 backdrop-blur-xs rounded-full p-2 text-primary shadow-xs border border-border/40">
                      <Icon className="size-4" />
                    </div>
                  </div>

                  {/* Badge & Botanical Title */}
                  <div className="mb-4">
                    <span className="inline-block bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full border border-primary/20 mb-3">
                      {item.tag}
                    </span>
                    <h3 className="font-serif text-2xl lg:text-3xl text-foreground font-medium mb-1">
                      {item.name}
                    </h3>
                    <p className="font-serif italic text-sm text-primary font-normal">
                      {item.botanical}
                    </p>
                  </div>

                  {/* Benefit Callout */}
                  <div className="bg-background/80 rounded-xl px-3.5 py-2.5 border border-border/60 mb-4">
                    <p className="text-xs font-semibold text-foreground/90 font-sans">
                      {item.benefit}
                    </p>
                  </div>

                  {/* Description Paragraph */}
                  <p className="text-sm sm:text-base text-foreground/80 leading-relaxed font-sans mb-6">
                    {item.description}
                  </p>
                </div>

                {/* Card Footer Link */}
                <div className="pt-4 border-t border-border/50 mt-auto">
                  <Button asChild variant="ghost" className="w-full justify-between px-0 text-primary hover:text-primary/80 hover:bg-transparent font-sans font-semibold text-sm group">
                    <Link to="/ShopPage">
                      <span>Explorar fórmulas con {item.name}</span>
                      <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </motion.article>;
        })}
        </div>

        {/* Bottom Educational Callout */}
        <motion.div initial={prefersReducedMotion ? {} : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-14 md:mt-20 rounded-2xl bg-secondary/60 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl text-center md:text-left">
            <h4 className="font-serif text-xl sm:text-2xl text-foreground mb-2">
              ¿Quieres descubrir el ritual adecuado para tu piel?
            </h4>
            <p className="text-sm sm:text-base text-foreground/80 font-sans">
              Combina el cuidado tópico de nuestras cremas con infusiones y tinturas para un bienestar integral desde adentro hacia afuera.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-6 text-sm font-semibold shadow-xs">
              <Link to="/ShopPage">
                Ver todos los productos
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}